/**
 * Enable/disable rules for + ADD OPTION and delete delivery option controls.
 *
 * Disable when the order matches `whitelisted_field_for_new_delivery_option`
 * on any of: customer code, associateSupplierId, splitSupplierId, orderNo —
 * unless the current user is in `override_edit_user_ids`, or the order is in
 * `orca_allow_md_order_nos`.
 */

export type NewDeliveryOptionWhitelist = {
  code: string[];
  associateSupplierId: string[];
  splitSupplierId: string[];
  orderNo: string[];
};

/** Which whitelist field caused the restriction. */
export type ManualDeliveryRestrictionReason =
  | "order"
  | "customer"
  | "associatedSupplier"
  | "supplier";

export type ManualDeliveryEditPermission = {
  canEdit: boolean;
  reason: ManualDeliveryRestrictionReason | null;
};

function parseIdList(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((id) => String(id).trim().toLowerCase()).filter(Boolean);
  }
  if (typeof raw !== "string" || !raw.trim()) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.map((id) => String(id).trim().toLowerCase()).filter(Boolean);
    }
  } catch {
    // plain / comma-separated string
  }
  return raw
    .split(",")
    .map((id) => id.trim().toLowerCase())
    .filter(Boolean);
}

function normalizeId(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "object") {
    const asAny = value as { $oid?: string; _id?: unknown; toString?: () => string };
    if (asAny.$oid) return String(asAny.$oid).trim().toLowerCase();
    if (typeof asAny.toString === "function") {
      const s = asAny.toString();
      if (s && s !== "[object Object]") return s.trim().toLowerCase();
    }
    if (asAny._id != null && asAny._id !== value) return normalizeId(asAny._id);
    return "";
  }
  return String(value).trim().toLowerCase();
}

export function parseNewDeliveryOptionWhitelist(
  raw: unknown,
): NewDeliveryOptionWhitelist {
  const empty: NewDeliveryOptionWhitelist = {
    code: [],
    associateSupplierId: [],
    splitSupplierId: [],
    orderNo: [],
  };

  if (raw == null || raw === "") return empty;

  let parsed: any = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return empty;
    }
  }

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return empty;
  }

  return {
    code: parseIdList(parsed.code),
    associateSupplierId: parseIdList(
      parsed.associateSupplierId ?? parsed.associatedSupplierId,
    ),
    splitSupplierId: parseIdList(parsed.splitSupplierId),
    orderNo: parseIdList(parsed.orderNo),
  };
}

export function getOrderCustomerCode(orderData: any): string {
  return normalizeId(orderData?.customer?.code);
}

export function getOrderNo(orderData: any): string {
  return normalizeId(orderData?.orderNo ?? orderData?.orderNumber ?? "");
}

export function getCustomerAssociateSupplierIds(orderData: any): string[] {
  const associations = orderData?.customer?.supplierAssociations;
  if (!Array.isArray(associations)) return [];
  return associations
    .map((assoc) =>
      normalizeId(
        assoc?.supplierId ?? assoc?.supplier?._id ?? assoc?.supplier?.id,
      ),
    )
    .filter(Boolean);
}

export function isUserAllowedToOverrideEdit(
  userId: string | undefined | null,
  overrideEditUserIdsRaw: unknown,
): boolean {
  if (!userId || overrideEditUserIdsRaw == null || overrideEditUserIdsRaw === "") {
    return false;
  }
  return parseIdList(overrideEditUserIdsRaw).includes(
    String(userId).trim().toLowerCase(),
  );
}

function reasonLabel(reason: ManualDeliveryRestrictionReason): string {
  switch (reason) {
    case "order":
      return "this order";
    case "customer":
      return "this customer";
    case "associatedSupplier":
      return "this associated supplier";
    case "supplier":
      return "this supplier";
  }
}

export function getAddManualDeliveryRestrictionMessage(
  reason: ManualDeliveryRestrictionReason | null | undefined,
): string {
  if (!reason) {
    return "Adding Manual Delivery Option is restricted";
  }
  return `Adding Manual Delivery Option is restricted for ${reasonLabel(reason)}`;
}

export function getDeleteDeliveryOptionRestrictionMessage(
  reason: ManualDeliveryRestrictionReason | null | undefined,
): string {
  if (!reason) {
    return "Deleting delivery option is restricted";
  }
  return `Deleting delivery option is restricted for ${reasonLabel(reason)}`;
}

export type WhitelistMatchParams = {
  whitelistRaw?: unknown;
  orderData: any;
  /** Explicit order number; takes precedence over orderData.orderNo. */
  orderNo?: string | null;
  splitSupplierId?: string | null;
};

/**
 * Returns which whitelist field matched, or null when none match.
 * Priority: orderNo → customer code → split supplier → associated supplier.
 */
export function getNewDeliveryOptionWhitelistMatchReason(
  params: WhitelistMatchParams,
): ManualDeliveryRestrictionReason | null {
  const whitelist = parseNewDeliveryOptionWhitelist(params.whitelistRaw);

  const orderNo = normalizeId(params.orderNo) || getOrderNo(params.orderData);
  if (orderNo && whitelist.orderNo.includes(orderNo)) {
    return "order";
  }

  const customerCode = getOrderCustomerCode(params.orderData);
  if (customerCode && whitelist.code.includes(customerCode)) {
    return "customer";
  }

  const splitSupplierId = normalizeId(params.splitSupplierId);
  if (splitSupplierId && whitelist.splitSupplierId.includes(splitSupplierId)) {
    return "supplier";
  }

  const associateIds = getCustomerAssociateSupplierIds(params.orderData);
  if (associateIds.some((id) => whitelist.associateSupplierId.includes(id))) {
    return "associatedSupplier";
  }

  return null;
}

export function matchesNewDeliveryOptionWhitelist(
  params: WhitelistMatchParams,
): boolean {
  return getNewDeliveryOptionWhitelistMatchReason(params) != null;
}

/**
 * Returns whether + ADD OPTION / delete are enabled, and why they are restricted.
 */
export function getManualDeliveryEditPermission(params: {
  orderData: any;
  /** Explicit order number; takes precedence over orderData.orderNo. */
  orderNo?: string | null;
  userId?: string | null;
  whitelistRaw?: unknown;
  /** Order numbers exempt from restriction (`orca_allow_md_order_nos`). */
  allowedOrderNosRaw?: unknown;
  allowedUserIdsRaw?: unknown;
  splitSupplierId?: string | null;
}): ManualDeliveryEditPermission {
  const { orderData, orderNo, userId, allowedUserIdsRaw, splitSupplierId } =
    params;

  if (isUserAllowedToOverrideEdit(userId, allowedUserIdsRaw)) {
    return { canEdit: true, reason: null };
  }

  // Configured order-level exceptions win over any restriction.
  const effectiveOrderNo = normalizeId(orderNo) || getOrderNo(orderData);
  if (
    effectiveOrderNo &&
    parseIdList(params.allowedOrderNosRaw).includes(effectiveOrderNo)
  ) {
    return { canEdit: true, reason: null };
  }

  const reason = getNewDeliveryOptionWhitelistMatchReason({
    whitelistRaw: params.whitelistRaw,
    orderData,
    orderNo,
    splitSupplierId,
  });

  if (reason) {
    return { canEdit: false, reason };
  }

  return { canEdit: true, reason: null };
}

/**
 * Returns true when + ADD OPTION and delete controls should be enabled.
 */
export function canEditManualDeliveryOptions(params: {
  orderData: any;
  /** Explicit order number; takes precedence over orderData.orderNo. */
  orderNo?: string | null;
  userId?: string | null;
  whitelistRaw?: unknown;
  allowedOrderNosRaw?: unknown;
  allowedUserIdsRaw?: unknown;
  splitSupplierId?: string | null;
}): boolean {
  return getManualDeliveryEditPermission(params).canEdit;
}
